/*
 * Copyright 2025 coze-dev Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { createBrowserRouter, Navigate } from 'react-router-dom';

import { SpaceSubModuleEnum } from '@coze-foundation/space-ui-adapter';
import { GlobalError } from '@coze-foundation/layout';
import { BaseEnum } from '@coze-arch/web-context';

import { Layout } from '../layout';
import {
  LoginPage,
  SpaceLayout,
  SpaceIdLayout,
  Develop,
  TeamAdmin,
  OAuthAdmin,
  JoinSpace,
  AgentIDELayout,
  AgentIDE,
  AgentPublishPage,
  Redirect,
  spaceSubMenu,
  exploreSubMenu,
  WorkflowPage,
  SearchPage,
  ProjectIDE,
  ProjectIDEPublish,
  Library,
  PluginLayout,
  PluginToolPage,
  PluginPage,
  KnowledgePreview,
  KnowledgeUpload,
  DatabaseDetail,
  ExplorePluginPage,
  ExploreTemplatePage,
} from './async-components';

// 说明：loader 返回的对象会作为路由数据（route data）被全局布局读取，
// 用于控制页面外壳的显示状态，各字段含义如下：
//   hasSider              是否显示侧边栏（默认 false）
//   requireAuth           是否需要登录鉴权（默认 false）
//   showMobileTips        是否显示“移动端不适配”的提示文案（默认 false）
//   subMenu               二级导航组件（配合 menuKey 使用）
//   menuKey               一级导航高亮项
//   subMenuKey            二级导航高亮项
//   pageModeByQuery       是否根据 URL 中 page_mode 参数决定页面展示模式（侧边栏模式 / 全屏弹窗模式）
//   requireBotEditorInit  是否需要初始化 Bot 编辑器（仅 Agent IDE 相关路由使用）
//   pageName              当前页面在 Agent IDE 布局中的页面名称
export const router: ReturnType<typeof createBrowserRouter> = createBrowserRouter([
  // ===================== 文档跳转路由 =====================
  // /open/docs/*：开放文档入口，重定向到 coze.cn 官方文档站（无需登录、无侧边栏）
  {
    path: '/open/docs/*',
    Component: Redirect,
    loader: () => ({
      hasSider: false,
      requireAuth: false,
    }),
  },
  // /docs/*：文档入口，同样重定向到 coze.cn 官方文档站
  {
    path: '/docs/*',
    Component: Redirect,
    loader: () => ({
      hasSider: false,
      requireAuth: false,
    }),
  },
  // /information/auth/success：信息授权成功后的回调页，重定向处理
  {
    path: '/information/auth/success',
    Component: Redirect,
    loader: () => ({
      hasSider: false,
      requireAuth: false,
    }),
  },

  // ===================== 主应用路由 =====================
  // 根路径，渲染全局布局 Layout（所有业务页面都作为其子路由渲染）
  {
    path: '/',
    Component: Layout,
    errorElement: <GlobalError />,
    children: [
      // 访问根路径时默认跳转到工作区 /space
      {
        index: true,
        element: <Navigate to="/space" replace />,
      },

      // 登录页：/sign，无需登录、无侧边栏
      {
        path: 'sign',
        Component: LoginPage,
        errorElement: <GlobalError />,
        loader: () => ({
          hasSider: false,
          requireAuth: false,
        }),
      },

      // 加入空间页：/join/:space_id，需要登录、无侧边栏
      {
        path: 'join/:space_id',
        Component: JoinSpace,
        errorElement: <GlobalError />,
        loader: () => ({
          hasSider: false,
          requireAuth: true,
        }),
      },

      // ===================== 工作区（空间）路由 =====================
      // /space：工作区布局，带侧边栏、需要登录，一级导航高亮“空间”
      {
        path: 'space',
        Component: SpaceLayout,
        loader: () => ({
          hasSider: true,
          requireAuth: true,
          subMenu: spaceSubMenu,
          menuKey: BaseEnum.Space,
        }),
        children: [
          // /space/:space_id：某个具体空间（团队）的布局容器
          {
            path: ':space_id',
            Component: SpaceIdLayout,
            children: [
              // 访问 /space/:space_id 时默认跳转到开发页 develop
              {
                index: true,
                element: <Navigate to="develop" replace />,
              },

              // 项目开发页：/space/:space_id/develop，工作区首页，二级导航高亮“开发”
              {
                path: 'develop',
                Component: Develop,
                loader: () => ({
                  subMenuKey: SpaceSubModuleEnum.DEVELOP,
                }),
              },

              // 组织空间页：/space/:space_id/team，管理成员与空间，二级导航高亮“组织空间”
              {
                path: 'team',
                Component: TeamAdmin,
                loader: () => ({
                  subMenuKey: SpaceSubModuleEnum.ORGANIZATION,
                }),
              },

              // OAuth 应用页：/space/:space_id/oauth，管理 OAuth 应用与用量统计，二级导航高亮“OAuth 应用”
              {
                path: 'oauth',
                Component: OAuthAdmin,
                loader: () => ({
                  subMenuKey: SpaceSubModuleEnum.OAUTH,
                }),
              },

              // ===================== Agent（Bot）IDE =====================
              // /space/:space_id/bot/:bot_id：Agent IDE 布局容器，无侧边栏、需要初始化 Bot 编辑器
              {
                path: 'bot/:bot_id',
                Component: AgentIDELayout,
                children: [
                  // 默认进入 Agent IDE 编辑器（BotEditor）
                  {
                    index: true,
                    Component: AgentIDE,
                  },
                  // Agent 发布页：/space/:space_id/bot/:bot_id/publish
                  {
                    path: 'publish',
                    children: [
                      {
                        index: true,
                        Component: AgentPublishPage,
                        loader: () => ({
                          hasSider: false,
                          requireBotEditorInit: false,
                          pageName: 'publish',
                        }),
                      },
                    ],
                  },
                ],
                loader: () => ({
                  hasSider: false,
                  showMobileTips: true,
                  requireBotEditorInit: true,
                  pageName: 'bot',
                }),
              },

              // ===================== 项目（Project）IDE =====================
              // 项目发布页：/space/:space_id/project-ide/:project_id/publish，无侧边栏
              {
                path: 'project-ide/:project_id/publish',
                loader: () => ({
                  hasSider: false,
                }),
                Component: ProjectIDEPublish,
              },
              // 项目 IDE 编辑器：/space/:space_id/project-ide/:project_id/*，无侧边栏
              {
                path: 'project-ide/:project_id/*',
                Component: ProjectIDE,
                loader: () => ({
                  hasSider: false,
                }),
              },

              // 资源库页：/space/:space_id/library，二级导航高亮“资源库”
              {
                path: 'library',
                Component: Library,
                loader: () => ({
                  subMenuKey: SpaceSubModuleEnum.LIBRARY,
                }),
              },

              // ===================== 知识库资源 =====================
              // /space/:space_id/knowledge，页面模式由 URL 的 page_mode 参数决定
              {
                path: 'knowledge',
                children: [
                  // 知识库预览页：/space/:space_id/knowledge/:dataset_id
                  {
                    path: ':dataset_id',
                    element: <KnowledgePreview />,
                  },
                  // 知识库上传页：/space/:space_id/knowledge/:dataset_id/upload
                  {
                    path: ':dataset_id/upload',
                    element: <KnowledgeUpload />,
                  },
                ],
                loader: () => ({
                  pageModeByQuery: true,
                }),
              },

              // ===================== 数据库资源 =====================
              // /space/:space_id/database，页面模式由 URL 的 page_mode 参数决定，移动端显示提示
              {
                path: 'database',
                children: [
                  // 数据库详情页：/space/:space_id/database/:table_id
                  {
                    path: ':table_id',
                    element: <DatabaseDetail />,
                  },
                ],
                loader: () => ({
                  showMobileTips: true,
                  pageModeByQuery: true,
                }),
              },

              // ===================== 插件资源 =====================
              // /space/:space_id/plugin/:plugin_id：插件资源布局容器
              {
                path: 'plugin/:plugin_id',
                Component: PluginLayout,
                children: [
                  // 默认进入插件详情页
                  {
                    index: true,
                    Component: PluginPage,
                  },
                  // 插件工具页：/space/:space_id/plugin/:plugin_id/tool/:tool_id
                  {
                    path: 'tool/:tool_id',
                    children: [
                      {
                        index: true,
                        Component: PluginToolPage,
                      },
                    ],
                  },
                ],
              },
            ],
          },
        ],
      },

      // ===================== 工作流路由 =====================
      // 工作流页面：/work_flow，无侧边栏、需要登录
      {
        path: 'work_flow',
        Component: WorkflowPage,
        loader: () => ({
          hasSider: false,
          requireAuth: true,
        }),
      },

      // ===================== 搜索路由 =====================
      // 搜索页：/search/:word，带侧边栏、需要登录
      {
        path: 'search/:word',
        Component: SearchPage,
        loader: () => ({
          hasSider: true,
          requireAuth: true,
        }),
      },

      // ===================== 探索（Explore）路由 =====================
      // /explore：探索页容器，带侧边栏、需要登录，一级导航高亮“探索”
      {
        path: 'explore',
        Component: null,
        loader: () => ({
          hasSider: true,
          requireAuth: true,
          subMenu: exploreSubMenu,
          menuKey: BaseEnum.Explore,
        }),
        children: [
          // 访问 /explore 时默认跳转到插件商店 plugin
          {
            index: true,
            element: <Navigate to="plugin" replace />,
          },
          // 插件商店：/explore/plugin
          {
            path: 'plugin',
            element: <ExplorePluginPage />,
            loader: () => ({
              type: 'plugin',
            }),
          },
          // 模板商店：/explore/template
          {
            path: 'template',
            element: <ExploreTemplatePage />,
            loader: () => ({
              type: 'template',
            }),
          },
        ],
      },
    ],
  },
]);
